import * as Sentry from "@sentry/react";
import { DefaultError, Mutation, Query } from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";

import handleSessionExpired from "@features/auth/handleSessionExpired";

class UnexpectedApiErrorMessage extends AxiosError {
  constructor(error: AxiosError) {
    super(
      error.message,
      error.code,
      error.config,
      error.request,
      error.response
    );
    this.name = "UnexpectedApiErrorMessage";
  }
}

function getAxiosFingerprint(error: AxiosError) {
  const genericUrl = error.config?.url
    ?.replace(/\?.*$/, "")
    .replace(/(\/\d+)/g, "/:id");
  return [
    "network-error",
    error.config?.method?.toUpperCase() ?? "unknown",
    genericUrl ?? "unknown",
    String(String(error.response?.status ?? error.code ?? "unknown")),
  ];
}

export const reportUnexpectedApiErrorMessage = (error: AxiosError) => {
  Sentry.withScope((scope) => {
    scope.setLevel("info");
    scope.setContext("response", {
      data: error.response?.data,
      status: String(error.response?.status),
      headers: error.response?.headers,
    });
    Sentry.captureException(new UnexpectedApiErrorMessage(error));
  });
};

const getExceptionLevel = (error: DefaultError): Sentry.SeverityLevel => {
  if (
    isAxiosError(error) &&
    error.response?.status &&
    ![403, 500].includes(error.response.status)
  ) {
    return "info";
  }
  return "error";
};

export const captureAxiosException = (error: AxiosError) => {
  if (!error) return;

  if (error.response?.status === 401) {
    handleSessionExpired();
    return;
  }
  Sentry.withScope((scope) => {
    scope.setLevel(getExceptionLevel(error));
    scope.setContext("request", {
      data: error.config?.data,
      response: error.response?.data,
      status: error.response?.status,
      headers: error.response?.headers,
    });
    if (isAxiosError(error)) {
      scope.setFingerprint(getAxiosFingerprint(error));
    }
    Sentry.captureException(error);
  });
};

export const captureMutationException = (
  error: DefaultError,
  mutation: Mutation<unknown, unknown, unknown, unknown>
) => {
  Sentry.withScope((scope) => {
    scope.setLevel(getExceptionLevel(error));
    scope.setContext("request", {
      data: error.config?.data,
      response: error.response?.data,
      status: error.response?.status,
      headers: error.response?.headers,
    });
    scope.setContext("mutation", {
      mutationId: mutation.mutationId,
      variables: mutation.state.variables,
      mutationKey: mutation.options?.mutationKey,
    });
    scope.setFingerprint(getAxiosFingerprint(error));
    scope.setTags({
      http_status: String(error.response?.status ?? "unknown"),
    });
    Sentry.captureException(error);
  });
};

export const captureQueryException = (
  error: DefaultError,
  query: Query<unknown, unknown, unknown, readonly unknown[]>
) => {
  Sentry.withScope((scope) => {
    scope.setLevel(getExceptionLevel(error));
    scope.setContext("query", {
      queryHash: query.queryHash,
      queryKey: query.queryKey,
    });
    scope.setFingerprint(getAxiosFingerprint(error));
    scope.setTags({
      http_status: String(error.response?.status ?? "unknown"),
    });
    Sentry.captureException(error);
  });
};
